import React, { useEffect, useRef, useState } from 'react';
import { FileText, Trash2 } from 'lucide-react';
import { Field, inputCls, grid2 } from '../shared/FormKit';
import {
  MAX_CERTIFICATE_PAGES, missingOf, listMissing, isReadableCertificate,
} from './CpdAutofill';

// ONE ROW = ONE CERTIFICATE = ONE RECORD (docs/modules/workflow-ld-cpd.md).
//
// Staff routinely scan several certificates into a single PDF, so an upload is
// not the unit of work — a certificate is. This module owns that translation
// for BOTH surfaces that file External CPD:
//
//   • Workflow → External CPD → New declaration  (one row → one declaration)
//   • Management → Training Records → External CPD → New record
//                                               (one row → one CPD record)
//
// The two surfaces MUST share this module — written separately they drift:
// one splits a multi-certificate PDF into rows while the other silently uses
// the FIRST certificate only and files the rest as nameless attachments — a
// two-course PDF becomes one record and half the hours vanish. Sharing the
// builder, the row-rebuild rule and the row UI means a PDF read
// one way on one screen can never be read another way on the other.

const DEFAULT_LABELS = {
  trainingTitle: 'Course / Seminar',
  certificateDate: 'Certificate Date',
  hours: 'Hours',
  categoryId: 'Category',
};

/** The `field=` key of one control in one row — the same string on both sides
 *  (the row's Field and the page's gap list), so a gap lights up its own box. */
export const cpdRowFieldKey = (rowKey, field) => `cpd.${rowKey}.${field}`;

/**
 * The row's empty required fields as `[{ key, label }]` for the page's
 * submit-time marking (formValidation). `rowKey` is the row's index in the
 * list — the component keys its Fields the same way. `prefix` names the
 * certificate when there is more than one ("Certificate 2 — Hours").
 */
export const cpdRowGaps = (row, rowKey, { labels = DEFAULT_LABELS, prefix = '' } = {}) => {
  const gaps = [];
  const add = (field) => gaps.push({
    key: cpdRowFieldKey(rowKey, field),
    label: `${prefix}${labels[field] || DEFAULT_LABELS[field]}`,
  });
  if (!String(row.trainingTitle || '').trim()) add('trainingTitle');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(row.certificateDate || '')) add('certificateDate');
  if (String(row.hours ?? '').trim() === '') add('hours');
  if (!row.categoryId) add('categoryId');
  return gaps;
};

/**
 * Gaps across a whole row list. `skip(row, index)` leaves out rows that are
 * not part of this save (HR's modal: rows already filed as records).
 */
export const cpdRowsGaps = (rows, { labels, skip } = {}) => rows.flatMap((r, i) => (
  skip && skip(r, i)
    ? []
    : cpdRowGaps(r, i, { labels, prefix: rows.length > 1 ? `Certificate ${i + 1} — ` : '' })
));

/**
 * Front-end mirror of the server's entry validation — the same three rules,
 * said before the upload rather than after it. Empty fields are also gaps
 * (above); the one rule only this catches is a non-positive hours value.
 */
export const cpdRowError = (row) => {
  if (!String(row.trainingTitle || '').trim()) return 'Course / seminar is required.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(row.certificateDate || '')) return 'Certificate date is required.';
  const hours = Number(row.hours);
  if (!Number.isFinite(hours) || hours <= 0) return 'Hours must be a number greater than 0.';
  // Every certificate files under its own category, picked by hand —
  // the reader never suggests one (no title-matching).
  if (!row.categoryId) return 'Category is required.';
  return null;
};

const emptyRow = (fileIndex, file, note) => ({
  fileIndex,
  filename: file.name,
  certIndex: 1,
  certCount: 1,
  trainingTitle: '',
  certificateDate: '',
  hours: '',
  categoryId: '',
  extracted: null,
  note,
});

/**
 * Turn "what the reader made of these files" into one editable row per
 * certificate. `certIndex`/`certCount` say where a row sat INSIDE its own
 * file, which is the only way the screen can answer "why did one PDF give me
 * two rows?".
 *
 * A file nothing could be read from still gets a row (a Word certificate, a
 * blurry photo, a PDF too long to send, or no reader configured at all): the
 * details are simply typed in.
 */
export const buildCpdRows = ({ files, certificates = [], fileOutcomes = [], oversized = [] }) =>
  (files || []).flatMap((file, fileIndex) => {
    const found = certificates.filter((c) => c.file === file);
    if (found.length > 0) {
      return found.map((c, i) => {
        const gaps = missingOf(c);
        return {
          fileIndex,
          filename: file.name,
          certIndex: i + 1,
          certCount: found.length,
          trainingTitle: c.trainingTitle || '',
          certificateDate: c.certificateDate || '',
          hours: c.hours != null ? String(c.hours) : '',
          categoryId: '',
          // Kept verbatim for the reviewer even if every field below is edited
          // afterwards — the detail page's "What the certificate says" card
          // puts the two side by side.
          extracted: {
            filename: c.filename,
            participantName: c.participantName,
            trainingTitle: c.trainingTitle,
            hours: c.hours,
            certificateDate: c.certificateDate,
            evidence: c.evidence || null,
            nameMatch: c.nameMatch || null,
          },
          participantName: c.participantName || null,
          staffSuggestion: c.staffSuggestion || null,
          note: gaps.length > 0
            ? `Could not read ${listMissing(gaps)} — please fill ${gaps.length > 1 ? 'them' : 'it'} in.`
            : null,
        };
      });
    }
    const tooLong = oversized.find((o) => o.name === file.name);
    const outcomeOf = fileOutcomes.find((o) => o.file === file);
    return [emptyRow(fileIndex, file, tooLong
      ? `${tooLong.pages} pages — too long to read automatically (limit ${MAX_CERTIFICATE_PAGES}). Please type the details in.`
      : `${outcomeOf?.reason || 'This file was not read automatically.'} Please type the details in.`)];
  });

/**
 * Rows kept in step with an `useCpdAutofill` result.
 *
 * Rebuilt at exactly two moments: when a read finishes (the busy flag's
 * falling edge — which covers a FAILED read too, so a dead reader still leaves
 * rows to type into), and when a selection with nothing readable in it arrives,
 * since no read will run for those at all. Anything else — a parent re-render,
 * a keystroke — must leave typed fields alone. This is the one genuinely
 * trap-laden part of both forms, which is why it lives in one place.
 *
 * Returns `[rows, setRows, generation]`; `generation` ticks on every rebuild so
 * a caller can clear its own per-attempt state (validation flags, save
 * results) without re-implementing the rule.
 */
export const useCpdRows = ({ files, autofill, enabled = true }) => {
  const { busy, certificates, files: fileOutcomes, rejected: oversized } = autofill;
  const [rows, setRows] = useState([]);
  const [generation, setGeneration] = useState(0);
  const wasBusy = useRef(false);
  const builtFor = useRef('');

  useEffect(() => {
    if (!enabled) return;
    const key = (files || [])
      .map((f) => `${f.name}:${f.size}:${f.lastModified}`).join('|');
    const readFinished = wasBusy.current && !busy;
    const newSelection = key !== builtFor.current;
    wasBusy.current = busy;
    builtFor.current = key;
    if (busy) return;
    const nothingToRead = !(files || []).some(isReadableCertificate);
    if (!readFinished && !(newSelection && nothingToRead)) return;

    setRows(buildCpdRows({ files, certificates, fileOutcomes, oversized }));
    setGeneration((g) => g + 1);
  }, [enabled, files, busy, certificates, fileOutcomes, oversized]);

  return [rows, setRows, generation];
};

/** "2 certificates in 1 file", "3 certificates in 2 files", "1 certificate" */
export const describeCpdRows = (rows) => {
  const fileCount = new Set(rows.map((r) => r.fileIndex)).size;
  if (rows.length === 1) return '1 certificate';
  return `${rows.length} certificates in ${fileCount} file${fileCount === 1 ? '' : 's'}`;
};

/**
 * The editable rows themselves.
 *
 *   rows      — from `useCpdRows`
 *   onChange  — (index, key, value)
 *   onRemove  — (index); omit to make rows un-removable
 *   attempted — show the per-row rule error (after a failed submit) once the
 *               row has no empty required field left; those mark themselves
 *               through the page's FormValidationProvider (keys from
 *               `cpdRowFieldKey`, gaps from `cpdRowsGaps`)
 *   disabled  — boolean, or (row, index) => boolean when only SOME rows are
 *               frozen (HR's modal locks a row once it has become a record)
 *   labels    — field wording, so each surface keeps its own vocabulary
 *   categoryOptions — the shared training-category list; each row picks
 *               its own, including two certificates out of the same file
 *   statusOf  — (row, index) => ReactNode, drawn where Remove sits; used by the
 *               HR modal to say which rows are already filed
 *   footerOf  — (row, index) => ReactNode, drawn under the fields (duplicate
 *               warnings, per-row failures)
 *
 * A single row renders plain — the bordered list only appears once there is
 * more than one thing to tell apart.
 */
const CpdCertificateRows = ({
  rows, onChange, onRemove, attempted = false, disabled = false,
  labels = DEFAULT_LABELS, categoryOptions = [], statusOf, footerOf,
}) => {
  const many = rows.length > 1;

  const row = (r, i) => {
    const frozen = typeof disabled === 'function' ? disabled(r, i) : disabled;
    const problem = attempted && !frozen && cpdRowGaps(r, i).length === 0 ? cpdRowError(r) : null;
    const status = statusOf ? statusOf(r, i) : null;
    const footer = footerOf ? footerOf(r, i) : null;
    return (
      <div key={`${r.fileIndex}-${r.certIndex}-${i}`} className={many ? 'p-4' : undefined}>
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <div className="min-w-0">
            {many && (
              <p className="text-sm font-medium text-slate-700">Certificate {i + 1}</p>
            )}
            <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
              <FileText className="w-3.5 h-3.5 shrink-0 text-slate-400" />
              <span className="truncate" title={r.filename}>{r.filename}</span>
              {/* The whole point of the split: this row and the one above it
                  came out of the SAME upload. */}
              {r.certCount > 1 && (
                <span className="shrink-0 text-slate-400">
                  — {r.certIndex} of {r.certCount} in this file
                </span>
              )}
            </p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            {status}
            {onRemove && !frozen && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-3.5 h-3.5" />Remove
              </button>
            )}
          </div>
        </div>

        {r.note && (
          <p className="mb-2 text-xs text-amber-700">
            {r.note}
          </p>
        )}

        <div className="space-y-4">
          <Field label={labels.trainingTitle} required field={cpdRowFieldKey(i, 'trainingTitle')}>
            <input
              type="text"
              value={r.trainingTitle}
              disabled={frozen}
              onChange={(e) => onChange(i, 'trainingTitle', e.target.value)}
              className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-500`}
            />
          </Field>
          <div className={grid2}>
            <Field label={labels.certificateDate} required field={cpdRowFieldKey(i, 'certificateDate')}>
              <input
                type="date"
                value={r.certificateDate}
                disabled={frozen}
                onChange={(e) => onChange(i, 'certificateDate', e.target.value)}
                className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-500`}
              />
            </Field>
            <Field label={labels.hours} required field={cpdRowFieldKey(i, 'hours')}>
              <input
                type="number"
                min="0"
                step="0.5"
                value={r.hours}
                disabled={frozen}
                onChange={(e) => onChange(i, 'hours', e.target.value)}
                className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-500`}
              />
            </Field>
          </div>
          <Field label={labels.categoryId || 'Category'} required field={cpdRowFieldKey(i, 'categoryId')}>
            <select
              value={r.categoryId || ''}
              disabled={frozen}
              onChange={(e) => onChange(i, 'categoryId', e.target.value)}
              className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-500`}
            >
              <option value="">Select a category…</option>
              {categoryOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
              {/* A frozen row may carry a title with no live option behind it
                  (HR's modal shows already-filed rows) — keep it readable. */}
              {r.categoryId && !categoryOptions.some((c) => String(c.id) === String(r.categoryId)) && (
                <option value={r.categoryId}>{r.categoryTitle || `#${r.categoryId}`}</option>
              )}
            </select>
          </Field>
        </div>

        {problem && <p className="mt-2 text-xs text-red-600">{problem}</p>}
        {footer}
      </div>
    );
  };

  if (!many) return rows.length === 1 ? row(rows[0], 0) : null;
  return (
    <div className="border border-slate-200 rounded-lg divide-y divide-slate-200">
      {rows.map(row)}
    </div>
  );
};

export default CpdCertificateRows;
